import { IState, Answer, ApplicationState } from "./state";
import { ActionTree, Store, CommitOptions } from 'vuex';
import * as api from '@/api';

type PayloadType = 'initApp' | 'setState' | 'rate' | 'setApplicationState' | 'comment';

export type ActionPayload<ActionData> = {
	type: PayloadType,
	payload: ActionData
}

interface ActionPayloadDispatch {
	<P extends ActionPayload<any>>(payloadWithType: P, options?: CommitOptions): void;
}

interface ActionPayloadCommit {
	<P extends ActionPayload<any>>(payloadWithType: P, options?: CommitOptions): void;
}

interface ActionContext<S, R> {
	dispatch: ActionPayloadDispatch;
	commit: ActionPayloadCommit;
	state: S;
	getters: any;
	rootState: R;
	rootGetters: any;
  }

type ActionHandler<ActionData> = (this: Store<IState>, injectee: ActionContext<IState, IState>, payload: ActionPayload<ActionData>) => any;
type ActionCreator<ActionData> = (data: ActionData) => ActionPayload<ActionData>;


// Action creators
export const initApp: ActionCreator<IState> = (state) => ({
	type: 'initApp',
	payload: state
})

// Action handlers
const initAppAction: ActionHandler<IState> = ({ commit }, { payload }) => {
	commit({
		type: 'setState',
		payload
	})
}

export interface IRatePayload {
	answer: Answer;
	nextState: ApplicationState;
	solution: string;
}
export const rate: ActionCreator<IRatePayload> = (payload) => ({
	type: 'rate',
	payload
})

const rateAction: ActionHandler<IRatePayload> = ({ commit, state, dispatch }, { payload }) => {
	const { answer, nextState, solution } = payload;
	commit({
		type: 'setState',
		payload: {
			answer
		}
	})
	dispatch(setApplicationState(nextState))

	api.rate(state.rateUrl, {
		answer,
		solution,
	})
	.then(response => {
		commit({
			type: 'setState',
			payload: {
				rateId: response.rateId
			}
		})
	})
}

export interface ICommentPayload {
	comment: string;
	nextState: ApplicationState
}

export const comment: ActionCreator<ICommentPayload> = payload => ({
	type: 'comment',
	payload
})

const commentAction: ActionHandler<ICommentPayload> = ({ commit, state, dispatch }, { payload }) => {
	const { comment, nextState } = payload;

	let sendingPromise = Promise.resolve();
	commit({
		type: 'setState',
		payload: {
			sending: true
		}
	})
	if(state.rateId) {
		sendingPromise = api.comment(state.commentUrl, {
			comment: comment,
			rateId: state.rateId
		}).then(response => {
			commit({
				type: 'setState',
				payload: {
					sent: response.success
				}
			})
		})
	}
	sendingPromise.then(() => {
		commit({
			type: 'setState',
			payload: {
				sending: false
			}
		})
		dispatch(setApplicationState(nextState))
	})
}

export const setApplicationState: ActionCreator<ApplicationState> = applicationState => ({
	type: 'setApplicationState',
	payload: applicationState
});

const setApplicationStateAction: ActionHandler<ApplicationState> = ({ commit }, { payload }) => {
	commit({
		type: 'setState',
		payload: {
			applicationState: payload
		}
	})
}

const actions: ActionTree<IState, IState> = {
	initApp: initAppAction,
	rate: rateAction,
	setApplicationState: setApplicationStateAction,
	comment: commentAction
}

export default actions;
